/**
 * Reads the assistant's answer stream over GraphQL: `agentMessageStream(turnId)`
 * (see `useAgentChat.graphql`) replaces the former NDJSON body of
 * `POST /api/agent/chat` (MEIS-13516, P1). `mapAgentStreamEvent` translates
 * one wire union member into this module's own, unchanged
 * `AgentStreamEvent` shape, so `useAgentChat.ts`/`agentChatAdapter.ts` keep
 * dispatching on the same `type` values as before; `readAgentMessageStream`
 * subscribes to an Apollo `Observable` and drives that mapping the same way
 * the former `readAgentStream` drove NDJSON lines — forwarding every
 * non-terminal event to `onEvent` and resolving/rejecting on the terminal
 * one (`result`/`error`, `AgentTurnUnavailableEvent` counting as an error).
 */
import type { FetchResult, Observer } from "@apollo/client";
import type { AgentMessageStreamSubscription } from "./useAgentChat.generated.js";

/**
 * The called tool's declared effect (`ai-assistant-chat` spec). Optional:
 * history stored before the backend sent it carries no `effect` — treat a
 * missing value as not a write. `labelKey` likewise: the tool's progress
 * label (a Tolgee key in namespace `Assistant`, see `agentToolTitle.ts`),
 * null for a tool without one and absent in history stored before labels —
 * both show the tool's `name` instead.
 */
export type AgentToolCall = {
  name: string;
  labelKey?: string | null;
  input: unknown;
  resultPreview: string;
  durationMs: number;
  effect?: "read" | "write" | "record";
  /**
   * What the entry shows for a refusal instead of `resultPreview`, which
   * instructs the model: a Tolgee key like `labelKey`. Null or absent on every
   * other result and in history stored before such keys, which show
   * `resultPreview`.
   */
  refusalKey?: string | null;
  /**
   * Where the call sits in its answer: a character offset into the stored
   * `content` (or the result's `reply`), the call standing between
   * `slice(0, position)` and the rest. Read from untyped JSON (`toolCalls` is
   * `JSON!`) and absent in history stored before positions, so it stays
   * `unknown` — read it through `toolCallPosition` (`agentChatAdapter.ts`).
   */
  position?: unknown;
};

export type AgentUiAction =
  | { type: "navigate"; path: string; label: string }
  | {
      type: "view";
      label: string;
      view: { component: string } & Record<string, unknown>;
    };

export type AgentTierBreakdown = Record<
  "light" | "heavy",
  { requests: number; costUsd: number }
>;

export type AgentChatResult = {
  messageId: string | null;
  reply: string;
  toolCalls: AgentToolCall[];
  uiActions: AgentUiAction[];
  stopReason: string | null;
  model: string;
  costUsd: number | null;
  promptTokens: number;
  completionTokens: number;
  tiers?: AgentTierBreakdown;
  escalatedAtIteration?: number | null;
};

/** One emission of the assistant's answer stream, in this module's own (wire-transport-independent) shape. */
export type AgentStreamEvent =
  | { type: "ping" }
  | { type: "conversation"; conversationId: string }
  /** `callId` is the model's own tool-call id — stable across a call's `tool_start`/`tool_done` pair, and the only safe way to match them when two calls to the same tool resolve out of order (`agentChatAdapter.ts`). */
  | {
      type: "tool_start";
      callId: string;
      name: string;
      /** The tool's progress label key (`AgentToolCall.labelKey`); null for a tool without one. */
      labelKey: string | null;
      input: unknown;
    }
  | { type: "tool_done"; callId: string; entry: AgentToolCall }
  /** A piece of the answer as the model writes it. */
  | { type: "text_delta"; delta: string }
  /** The text streamed so far is not the answer (tools follow, or it is rewritten). */
  | { type: "text_discard" }
  | ({ type: "result" } & AgentChatResult)
  | { type: "error"; error: string };

export class AgentStreamError extends Error {}

/**
 * The stream ended with the turn's own `AgentChatErrorEvent`: the turn ran,
 * failed, and the backend stored the user message and a failed answer (with the
 * tool calls so far). Unlike a refusal or a lost connection, resending the
 * message would duplicate what was stored (and repeat its writes).
 */
export class AgentStreamStoredFailureError extends AgentStreamError {}

/**
 * The stream ended with the backend's `AgentTurnUnavailableEvent`; its message
 * is the event's `reason`. Before the turn started it is a refusal; after it
 * started, only `AGENT_TURN_TAKEN_REASON` is a lost connection — any other
 * reason (e.g. the time limit) is shown as the error.
 */
export class AgentStreamUnavailableError extends AgentStreamError {}

/**
 * Reading the stored answer of a lost stream ended at its deadline, or with
 * polls that never saw whether the conversation still runs: the turn may still
 * store its answer later — or never. Handled as possibly stored: the message
 * is kept (marked `maybeStored`, left out of the recovery baseline), no retry.
 */
export class AgentStreamRecoveryTimeoutError extends AgentStreamError {}

/**
 * `readAgentMessageStream` heard nothing at all — not even a ping — for its
 * `silenceTimeoutMs`: the backend that runs the turn may be gone without
 * closing the connection (the SSE link would retry forever). Not a failure of
 * the turn by itself; the caller decides whether to recover the stored answer.
 */
export class AgentStreamSilentError extends AgentStreamError {}

/** `readAgentMessageStream` stopped reading because its `signal` was aborted — not a failure of the turn. */
export class AgentStreamAbortedError extends Error {}

type AgentMessageStreamEvent =
  AgentMessageStreamSubscription["agentMessageStream"];

/**
 * Translates one `agentMessageStream` union member into this module's own
 * `AgentStreamEvent` shape. `AgentTurnUnavailableEvent` (the subscription's
 * own signal that this `turnId` cannot be run — already taken or delivered)
 * maps onto `error`: there is nothing left to stream, and the caller's
 * existing error handling is exactly the right response.
 */
export function mapAgentStreamEvent(
  event: AgentMessageStreamEvent
): AgentStreamEvent {
  switch (event.__typename) {
    case "AgentPingEvent":
      return { type: "ping" };
    case "AgentConversationEvent":
      return { type: "conversation", conversationId: event.conversationId };
    case "AgentToolStartEvent":
      return {
        type: "tool_start",
        callId: event.callId,
        name: event.name,
        labelKey: event.labelKey ?? null,
        input: event.input,
      };
    case "AgentToolDoneEvent":
      return {
        type: "tool_done",
        callId: event.callId,
        entry: {
          name: event.name,
          labelKey: event.labelKey ?? null,
          input: event.input,
          resultPreview: event.resultPreview,
          durationMs: event.durationMs,
          effect: event.effect,
          refusalKey: event.refusalKey ?? null,
        },
      };
    case "AgentTextDeltaEvent":
      return { type: "text_delta", delta: event.delta };
    case "AgentTextDiscardEvent":
      return { type: "text_discard" };
    case "AgentChatResultEvent":
      return {
        type: "result",
        messageId: event.messageId ?? null,
        reply: event.reply,
        toolCalls: event.toolCalls as AgentToolCall[],
        uiActions: event.uiActions as AgentUiAction[],
        stopReason: event.stopReason ?? null,
        model: event.model,
        costUsd: event.costUsd ?? null,
        promptTokens: event.promptTokens,
        completionTokens: event.completionTokens,
        tiers: event.tiers as AgentTierBreakdown,
        escalatedAtIteration: event.escalatedAtIteration,
      };
    case "AgentChatErrorEvent":
      return { type: "error", error: event.error };
    case "AgentTurnUnavailableEvent":
      return { type: "error", error: event.reason };
  }
}

/** The minimal shape `readAgentMessageStream` needs from Apollo's subscription `Observable` — easy to fake in a test. */
export type AgentMessageStreamObservable = {
  subscribe(observer: Observer<FetchResult<AgentMessageStreamSubscription>>): {
    unsubscribe(): void;
  };
};

/**
 * Subscribes to `observable`, mapping every emission through
 * `mapAgentStreamEvent` and forwarding every non-terminal event to
 * `onEvent` — mirroring the former `readAgentStream`'s NDJSON-line
 * contract. `result` and `error` (including a mapped
 * `AgentTurnUnavailableEvent`) are terminal and are not handed to `onEvent`:
 * the former resolves this call, the latter rejects it with the server's
 * reason. A GraphQL-error-only emission, a transport error, or the stream
 * completing without either is an error too. Always unsubscribes once
 * settled, so the SSE link's auto-resubscribe (after a dropped connection)
 * never re-runs an already-delivered turn. Aborting `signal` stops reading
 * too (unsubscribes) and rejects with `AgentStreamAbortedError`. With
 * `silenceTimeoutMs`, a stream that emits nothing (a ping counts) for that
 * long is dropped and rejects with `AgentStreamSilentError`.
 */
export function readAgentMessageStream(
  observable: AgentMessageStreamObservable,
  onEvent: (event: AgentStreamEvent) => void,
  signal?: AbortSignal,
  silenceTimeoutMs?: number
): Promise<AgentChatResult> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let subscription: { unsubscribe(): void } | null = null;
    // A settle reached while `observable.subscribe(...)` below is still
    // running (an observable that emits synchronously on subscribe, as a
    // cache-replayed or test-fake one can) finds `subscription` still
    // unassigned — the assignment below only lands once `subscribe(...)`
    // returns. Deferred here instead of dropped, so it still unsubscribes
    // the moment that assignment happens.
    let unsubscribeOnceAssigned = false;
    let silenceTimer: ReturnType<typeof setTimeout> | null = null;
    const settle = (run: () => void) => {
      if (settled) return;
      settled = true;
      if (silenceTimer) clearTimeout(silenceTimer);
      if (subscription) subscription.unsubscribe();
      else unsubscribeOnceAssigned = true;
      run();
    };

    const armSilenceTimer = () => {
      if (settled || silenceTimeoutMs === undefined) return;
      if (silenceTimer) clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        settle(() =>
          reject(
            new AgentStreamSilentError(
              "The connection went silent before the answer arrived."
            )
          )
        );
      }, silenceTimeoutMs);
    };
    armSilenceTimer();

    subscription = observable.subscribe({
      next: result => {
        if (settled) return;
        armSilenceTimer();
        if (result.errors && result.errors.length > 0) {
          settle(() =>
            reject(
              new AgentStreamError(
                result.errors?.map(e => e.message).join("; ") ?? "unknown error"
              )
            )
          );
          return;
        }
        const raw = result.data?.agentMessageStream;
        if (!raw) return;
        const event = mapAgentStreamEvent(raw);
        if (event.type === "error") {
          settle(() =>
            reject(
              raw.__typename === "AgentChatErrorEvent"
                ? new AgentStreamStoredFailureError(event.error)
                : raw.__typename === "AgentTurnUnavailableEvent"
                  ? new AgentStreamUnavailableError(event.error)
                  : new AgentStreamError(event.error)
            )
          );
          return;
        }
        if (event.type === "result") {
          const { type: _type, ...rest } = event;
          settle(() => resolve(rest));
          return;
        }
        onEvent(event);
      },
      error: err => {
        settle(() =>
          reject(err instanceof Error ? err : new AgentStreamError(String(err)))
        );
      },
      complete: () => {
        settle(() =>
          reject(
            new AgentStreamError(
              "The connection ended before the answer arrived."
            )
          )
        );
      },
    });
    if (unsubscribeOnceAssigned) subscription.unsubscribe();
    const onAbort = () =>
      settle(() =>
        reject(new AgentStreamAbortedError("Stopped reading the answer."))
      );
    if (signal?.aborted) onAbort();
    else signal?.addEventListener("abort", onAbort, { once: true });
  });
}
